import gql from 'graphql-tag';
import { apolloClient as apollo } from '@/vue-apollo';
import { isNil } from '@/shared/utils';
import { PROJECT_CARBON_FIELD, EXPENSE_GQL, FIELDS_BUDGET_USER_EXPENSE_SHEET, EXPENSE_TYPE } from '@/shared/constants';
import _ from 'lodash';

const expensePrincipalColumn = `
	id
	date
	type
	invoiceNumber
	description
	detailedDescription
	amountTotal
	department
	supplierId
	supplierName
	validated
	createdAt
	itemsMerged
	toRefund
	user {
		id
		name
		firstName
	}
`;

const expenseItemRelativeColumn = `
	id
	description
	detailedDescription
	date
	amount
	amountVat
	amountTotal
	quantity
`;

const documentsRelativeColumnForExpense = `
	documents {
		id
		xid
		ext
		parentType
		originalFileName
		additionalDocumentType
	}
`;

const poRelativeColumnForExpense = `
	purchaseOrders {
		id
		poReference
		supplierId
		supplierName
		${documentsRelativeColumnForExpense}
	}
`;

const poRelativeColumnForExpenseItems = `
	poId
	poItemId
	category
	costCenter
	categoryName
`;

const flagColumn = `
	id
	flagId
	value
	name
	short
	factor
	amount
	payCode
	color
	allowanceRate
	bookmark
	category
	digitalSubmission
	disabled
	costCenter
	forScreen
	fieldType
	defaultValue
	jsFormula
	type
`;

const columnProjectFlag = `projectFlagItems {
	id
	taxeFlags {
		${flagColumn}
	}
	customFlags {
		${flagColumn}
	}
}`;

const columnForSpecificExpenseItems = `
	${expenseItemRelativeColumn}
	carbon {
		${PROJECT_CARBON_FIELD}
	}
	${columnProjectFlag}
	${poRelativeColumnForExpenseItems}
`;

const columnExpense = `
	${expensePrincipalColumn}
	comment
	${documentsRelativeColumnForExpense}
	${poRelativeColumnForExpense}
	expenseItem {
		${columnForSpecificExpenseItems}
	}
`;

const columnExpenseForOcr = `
	${expensePrincipalColumn}
	${documentsRelativeColumnForExpense}
	expenseItem {
	  	${expenseItemRelativeColumn}
		action
	  	${poRelativeColumnForExpenseItems}
		projectFlagItems {
			id
			taxeFlags {
				${flagColumn}
			}
		}
	}
	${poRelativeColumnForExpense}
`;

export const newExpense = async (expenseType = 0, purchaseOderId = null) => {
	const mutation = gql`
		mutation ($expenseType: ID!, $purchaseOderId: ID) {
			NewExpense(ExpenseType: $expenseType, PurchaseOrderId: $purchaseOderId) {
				id
				date
				department
				user {
					id
					name
					firstName
				}
			}
		}
	`;
	const { data } = await apollo.mutate({
		mutation,
		variables: {
			expenseType,
			purchaseOderId
		}
	});
	return data.NewExpense;
};

export const getMyExpenses = async (validated = null, advancedGlobalFilter = {}) => {
	const query = gql`
		query ($validated: Int, $advancedGlobalFilter: AdvancedGlobalFilterInput) {
			MyExpenses(Validated: $validated,AdvancedGlobalFilter: $advancedGlobalFilter) {
				${columnExpense}
			}
		}
	`;
	const {
		data: { MyExpenses }
	} = await apollo.query({
		query,
		variables: {
			validated,
			advancedGlobalFilter
		},
		fetchPolicy: 'no-cache'
	});

	return MyExpenses;
};

export const getSpecificExpense = async (expenseId) => {
	const query = gql`
		query ($expenseId: ID!) {
			GetExpense(ExpenseId: $expenseId) {
				${columnExpense}
			}
		}
	`;
	const {
		data: { GetExpense }
	} = await apollo.query({
		query,
		variables: {
			expenseId
		},
		fetchPolicy: 'no-cache'
	});

	return GetExpense;
};

export const getInvoiceEntryForCrew = async (advancedGlobalFilter = {}, isSubmitted = false) => {
	const QUERY_GET_PROJECT_USER_EXPENSE_SHEET = gql`
		query GetProjectUserExpenseSheet($filter: AdvancedGlobalFilterInput) {
			GetProjectUserExpenseSheet(Filter: $filter) {
				${FIELDS_BUDGET_USER_EXPENSE_SHEET}
			}
		}
	`;
	const filter = {
		...advancedGlobalFilter,
		activateCrewAccessRestriction: true,
		arrayType: [{ type: EXPENSE_TYPE.INVOICE }]
	};
	isSubmitted ? (filter.submitted = true) : (filter.notSubmitted = true);
	try {
		const {
			data: { GetProjectUserExpenseSheet }
		} = await apollo.query({
			query: QUERY_GET_PROJECT_USER_EXPENSE_SHEET,
			variables: { filter },
			fetchPolicy: 'no-cache'
		});
		return GetProjectUserExpenseSheet
			? GetProjectUserExpenseSheet.map((item) => {
					return { ...item.expense, crew_member: item.expense.user.name + ' ' + item.expense.user.firstName };
			  })
			: [];
	} catch (e) {
		console.error('error on getInvoiceEntryForCrew isSubmitted = ' + isSubmitted, e);
		return [];
	}
};

export const deleteMyExpense = async (expenseId) => {
	const mutation = gql`
		mutation ($expenseId: ID!) {
			DelExpense(ExpenseId: $expenseId)
		}
	`;
	const {
		data: { DelExpense }
	} = await apollo.mutate({
		mutation,
		variables: {
			expenseId: +expenseId
		}
	});
	return DelExpense;
};

export const updateExpense = async (expenseId, expenseInput, expenseItems = [], expenseItemsOperation = 0, userID = null) => {
	const mutation = gql`
		mutation ($expenseId: ID!, $expenseInput: ExpenseInput!, $expenseItems: [ExpenseItemInput], $expenseItemsOperation: Int!, $userID: Int) {
			UpdExpense(
				ExpenseId: $expenseId
				UpdatedExp: $expenseInput
				ExpenseItems: $expenseItems
				ExpenseItemsOperation: $expenseItemsOperation
				UserId: $userID
			) {
				id
			}
		}
	`;
	const {
		data: { UpdExpense }
	} = await apollo.mutate({
		mutation,
		variables: {
			expenseId: +expenseId,
			expenseInput,
			expenseItems,
			expenseItemsOperation,
			userID: userID ? +userID : null
		}
	});
	return UpdExpense;
};

export const restoreExpenseItemsOfExpense = async (expenseId) => {
	const mutation = gql`
		mutation ($expenseId: ID!) {
			RestoreExpenseItemsOfExpenseFromJson(ExpenseId: $expenseId) {
				${expenseItemRelativeColumn}
				action
				carbon {
				${PROJECT_CARBON_FIELD}
				}
				${poRelativeColumnForExpenseItems}
			}
		}
	`;
	const {
		data: { RestoreExpenseItemsOfExpenseFromJson }
	} = await apollo.mutate({
		mutation,
		variables: {
			expenseId: +expenseId
		}
	});
	return RestoreExpenseItemsOfExpenseFromJson;
};

export const mergeExpenseItemsOfExpense = async (expenseId, expenseInput) => {
	const mutation = gql`
		mutation ($expenseId: ID!, $expenseInput: ExpenseInput!) {
			MergeExpenseItemsOfExpense(ExpenseId: $expenseId, Expense: $expenseInput) {
				${expenseItemRelativeColumn}
				action
				carbon {
					${PROJECT_CARBON_FIELD}
				}
				${poRelativeColumnForExpenseItems}
			}
		}
	`;
	const {
		data: { MergeExpenseItemsOfExpense }
	} = await apollo.mutate({
		mutation,
		variables: {
			expenseId: +expenseId,
			expenseInput
		}
	});
	return MergeExpenseItemsOfExpense;
};

export const passExpenseToOcr = async (docXids, withDetails = true) => {
	const mutation = gql`
		mutation ($docXid: [String!]!, $withDetails: Boolean!) {
			ImportExpensesFromDocument(DocXid: $docXid, WithDetails: $withDetails) {
				${columnExpenseForOcr}
			}
		}
	`;
	const {
		data: { ImportExpensesFromDocument }
	} = await apollo.mutate({
		mutation,
		variables: {
			docXid: docXids,
			withDetails: withDetails
		}
	});
	return ImportExpensesFromDocument;
};

export const sendSelectedExpensesForValidation = async (expenseTitle, expenseData) => {
	try {
		const mutation = gql`
			mutation ($expenseTitle: String, $expenseData: [SendExpenseItemInput]!) {
				SendExpenseSlice(ExpenseTitle: $expenseTitle, ExpenseData: $expenseData)
			}
		`;
		const { data } = await apollo.mutate({
			mutation,
			variables: {
				expenseTitle,
				expenseData
			},
			fetchPolicy: 'no-cache'
		});

		return data.SendExpenseSlice;
	} catch (e) {
		console.log({ e });
	}
};

export const ChangeStatusExpensesProcess = async (expenseArrayId, valueOfProcessToChange) => {
	try {
		const mutation = gql`
			mutation ($ExpenseArrayId: [ID!]!, $ValueOfProcessToChange: Boolean!) {
				ChangeStatusExpensesProcess(ExpenseArrayId: $ExpenseArrayId, ValueOfProcessToChange: $ValueOfProcessToChange) {
					id
					date
					type
					description
					kgCoTwo
					amount
					amountVat
					amountTotal
					comment
					validated
					departmentName
					functionName
					encoderId
					encoder {
						id
						name
						firstName
						fullName
					}
					user {
						id
						name
						firstName
					}
					expenseItem {
						id
						category
						costCenter
						categoryName
						missingProjectFlag {
							id
							name
							short
							mandatoryFlag
						}
					}
				}
			}
		`;

		let variables = {
			ExpenseArrayId: expenseArrayId,
			ValueOfProcessToChange: valueOfProcessToChange
		};

		const {
			data: { ChangeStatusExpensesProcess }
		} = await apollo.mutate({
			mutation,
			variables
		});

		return ChangeStatusExpensesProcess;
	} catch (e) {
		console.error({ e });
	}
};

export const sendAllExpensesForValidation = async (ExpenseTitle) => {
	try {
		const mutation = gql`
			mutation ($ExpenseTitle: String) {
				SendAllExpenses(ExpenseTitle: $ExpenseTitle)
			}
		`;
		const { data } = await apollo.mutate({
			mutation,
			variables: {
				ExpenseTitle: ExpenseTitle
			},
			fetchPolicy: 'no-cache'
		});

		return data.SendAllExpenses;
	} catch (e) {
		console.log({ e });
	}
};

export const getInvoicesItems = async (expenseId = -1, advancedGolbalFilter = {}) => {
	const query = gql`
		query ($expenseId: ID!, $advancedGolbalFilter: AdvancedGlobalFilterInput) {
			GetExpenseItemList(ExpenseId: $expenseId,AdvancedGolbalFilter: $advancedGolbalFilter) {
				${columnForSpecificExpenseItems}
				${documentsRelativeColumnForExpense}
			}
		}
	`;

	const {
		data: { GetExpenseItemList }
	} = await apollo.query({
		query,
		variables: {
			expenseId,
			advancedGolbalFilter
		},
		fetchPolicy: 'no-cache'
	});

	return GetExpenseItemList;
};

export const getExpenseItems = async (expenseId = -1, advancedGlobalFilter = {}) => {
	if (_.isNil(expenseId) || !_.isNumber(expenseId)) throw new Error('Invalid ID');

	const query = gql`
		query ($expenseId: ID!, $advancedGlobalFilter: AdvancedGlobalFilterInput) {
			GetExpenseItemList(ExpenseId: $expenseId, AdvancedGolbalFilter: $advancedGlobalFilter) {
				${columnExpenseItem}
				carbon {
					${PROJECT_CARBON_FIELD}
					}
			}
		}
	`;

	const {
		data: { GetExpenseItemList }
	} = await apollo.query({
		query,
		variables: {
			expenseId,
			advancedGlobalFilter
		},
		fetchPolicy: 'no-cache'
	});

	return GetExpenseItemList;
};

export const getExpenseItemsForDepartmentAndCategoryList = async (DepartmentNumber, CategoryNumber) => {
	const query = gql`
		query ($DepartmentNumber: ID!, $CategoryNumber: ID!) {
			GetExpenseItemsForDepartmentAndCategoryList(DepartmentNumber: $DepartmentNumber, CategoryNumber: $CategoryNumber) {
				${columnExpenseItem}\n${EXPENSE_GQL}
			}
		}
	`;

	const {
		data: { GetExpenseItemsForDepartmentAndCategoryList }
	} = await apollo.query({
		query,
		variables: {
			DepartmentNumber,
			CategoryNumber
		},
		fetchPolicy: 'no-cache'
	});

	return GetExpenseItemsForDepartmentAndCategoryList;
};

export const validateMultipleExpense = async (expenseIds, comment = null, sendForValidation = null) => {
	const mutation = gql`
		mutation ($expenseIds: [ID!]!, $comment: String, $sendForValidation: Boolean) {
			ValidateExpenses(ExpenseIds: $expenseIds, Comment: $comment, SendForValidation: $sendForValidation) {
				id
			}
		}
	`;

	const {
		data: { ValidateExpense }
	} = await apollo.mutate({
		mutation,
		variables: {
			expenseIds,
			comment,
			sendForValidation
		}
	});

	return ValidateExpense;
};

export const validateExpense = async (expenseId, comment, sendForValidation = null) => {
	const mutation = gql`
		mutation ($expenseId: ID!, $comment: String, $sendForValidation: Boolean) {
			ValidateExpense(ExpenseId: $expenseId, Comment: $comment, SendForValidation: $sendForValidation) {
				id
				amountTotal
				comment
				validated
			}
		}
	`;

	const {
		data: { ValidateExpense }
	} = await apollo.mutate({
		mutation,
		variables: {
			expenseId,
			comment,
			sendForValidation
		},
		fetchPolicy: 'no-cache'
	});

	return ValidateExpense;
};

export const validateExpenseItem = async (expenseItemId) => {
	const mutation = gql`
		mutation ($expenseItemId: ID!) {
			ValidateExpenseItem(ExpenseItemId: $expenseItemId) {
				id
				allItemsTotal
			}
		}
	`;

	const {
		data: { ValidateExpenseItem }
	} = await apollo.mutate({
		mutation,
		variables: {
			expenseItemId
		},
		fetchPolicy: 'no-cache'
	});

	return ValidateExpenseItem;
};

export const undoRejectExpenseItem = async (expenseItemId) => {
	const mutation = gql`
		mutation ($expenseItemId: ID!) {
			UndoRejectExpenseItem(ExpenseItemId: $expenseItemId) {
				id
				allItemsTotal
			}
		}
	`;

	const {
		data: { UndoRejectExpenseItem }
	} = await apollo.mutate({
		mutation,
		variables: {
			expenseItemId
		},
		fetchPolicy: 'no-cache'
	});

	return UndoRejectExpenseItem;
};

export const rejectExpense = async (expenseId, comment) => {
	const mutation = gql`
		mutation ($expenseId: ID!, $comment: String) {
			RejectExpense(ExpenseId: $expenseId, Comment: $comment) {
				id
				amountTotal
				comment
				validated
			}
		}
	`;

	const {
		data: { RejectExpense }
	} = await apollo.mutate({
		mutation,
		variables: {
			expenseId,
			comment
		},
		fetchPolicy: 'no-cache'
	});

	return RejectExpense;
};

export const rejectExpenses = async (expenseIds, comment) => {
	const mutation = gql`
		mutation ($expenseIds: [ID!]!, $comment: String) {
			RejectExpenses(ExpenseIds: $expenseIds, Comment: $comment) {
				id
				amountTotal
				comment
				validated
			}
		}
	`;

	const {
		data: { RejectExpenses }
	} = await apollo.mutate({
		mutation,
		variables: {
			expenseIds,
			comment
		}
	});

	return RejectExpenses;
};

export const rejectExpenseItem = async (expenseItemId, comment) => {
	const mutation = gql`
		mutation ($expenseItemId: ID!, $comment: String) {
			RejectExpenseItem(ExpenseItemId: $expenseItemId, Comment: $comment) {
				${columnExpenseItem}
			}
		}
	`;

	const {
		data: { RejectExpenseItem }
	} = await apollo.mutate({
		mutation,
		variables: {
			expenseItemId,
			comment
		},
		fetchPolicy: 'no-cache'
	});

	return RejectExpenseItem;
};

export const getExpenseToValidate = async (advancedGolbalFilter = {}) => {
	const query = gql`
		query ($advancedGolbalFilter: AdvancedGlobalFilterInput) {
			GetExpenseToValidate(AdvancedGolbalFilter: $advancedGolbalFilter) {
				id
				type
				description
				date
				createdAt
				invoiceNumber
				itemsMerged
				amount
				amountVat
				amountTotal
				comment
				validated
				kgCoTwo
				department
				departmentName
				supplierId
				supplierName
				allExpenseItemIsInContract
				user {
					id
					name
					firstName
					email
				}
				encoderId
				encoder {
					id
					name
					fullName
					firstName
				}
				function
				functionName
				${documentsRelativeColumnForExpense}
				${poRelativeColumnForExpense}
			}
		}
	`;

	const {
		data: { GetExpenseToValidate }
	} = await apollo.query({
		query,
		variables: {
			advancedGolbalFilter
		},
		fetchPolicy: 'no-cache'
	});

	return GetExpenseToValidate;
};

export const getExpenseItem = async (expenseItemId = 0) => {
	const query = gql`
			query ($expenseItemId: ID!) {
				GetExpenseItem(ExpenseItemId: $expenseItemId) {
					${columnExpenseItem}
				}
			}
    `;

	const {
		data: { GetExpenseItem }
	} = await apollo.query({
		query,
		variables: {
			expenseItemId: +expenseItemId
		},
		fetchPolicy: 'no-cache'
	});

	return GetExpenseItem;
};

/**
 * Sends a GraphQL mutation to get expense items from an image or PDF document.
 *
 * @param {Array<String>|String} payload - Document identifier(s), without file extension. If multiple, provide an array.
 * @param {Object} [options={}]
 *
 * @returns {Promise<Array<{amountTotal: number, amountVat: number, description: string, date: string}>|null>}
 * Returns a promise that resolves to an array of expense items, or null if an error occurs.
 * @returns {Promise<Array<{amountTotal: number, amountVat: number, description: string, date: string}>|null>}
 * Returns a promise that resolves to an array of expense items, or null if an error occurs.
 * Each expense item is an object with properties field1, field2, and field3.
 *
 * @throws {ApolloError} If the GraphQL mutation fails, this function will log the error and rethrow it.
 *
 * @async
 */
export const expenseItemsFromImageOrPdf = async (payload, options = {}) => {
	/**
	 * @deftype {?Boolean} [OptionsState=false]
	 *
	 * @type {OptionsState} multipleExpenseItem
	 * @type {OptionsState} splitTVA
	 */
	const multipleExpenseItem = options.multipleExpenseItem || false;
	const splitTVA = options.splitTVA || false;
	// const userId = options.userId || null;

	// try {
	const mutation = gql`
		mutation ($DocXid: [String!]!, $MultipleExpenseItem: Boolean!, $SplitTVA: Boolean) {
			ExpenseItemsFromImageOrPdf(DocXid: $DocXid, MultipleExpenseItem: $MultipleExpenseItem, SplitTVA: $SplitTVA) {
				id
				amountTotal
				amountVat
				description
				supplierId
				supplierName
				department
				category
				paiementType
				paiementTypeName
				date
			}
		}
	`;
	const { data } = await apollo.mutate({
		mutation,
		variables: {
			DocXid: payload,
			MultipleExpenseItem: multipleExpenseItem,
			SplitTVA: splitTVA
			// UserId: userId,
		}
	});

	return data.ExpenseItemsFromImageOrPdf;
	// } catch (e) {
	// 	console.error({ e });
	// }
};

/**
 * @param {Object} payload
 */
export const generateExpenseRef = async (payload) => {
	try {
		const { data } = await apollo.mutate({
			mutation: gql`
				mutation ($AddedExpenseItem: ExpenseItemInput!) {
					NewExpenseItem(AddedExpenseItem: $AddedExpenseItem) {
						id
					}
				}
			`,
			variables: { AddedExpenseItem: payload },
			fetchPolicy: 'no-cache'
		});
		return data.NewExpenseItem;
	} catch (e) {
		console.error({ e });
	}
};

/**
 * @param {!String|Number} expenseId
 */
export const deleteExpense = async (expenseId) => {
	if (isNil(expenseId)) return;

	try {
		const { data } = await apollo.mutate({
			mutation: gql`
				mutation ($ExpenseItemId: ID!) {
					DelExpenseItem(ExpenseItemId: $ExpenseItemId) {
						id
					}
				}
			`,
			variables: { ExpenseItemId: parseInt(expenseId) }
		});

		return data;
	} catch (e) {
		console.error({ e });
	}
};

export const deleteExpenses = async (expenseIds) => {
	if (isNil(expenseIds)) return;

	try {
		const { data } = await apollo.mutate({
			mutation: gql`
				mutation ($ExpenseIds: [ID!]!) {
					DelExpenses(ExpenseIds: $ExpenseIds)
				}
			`,
			variables: { ExpenseIds: expenseIds }
		});

		return data;
	} catch (e) {
		console.error({ e });
	}
};

/**
 * @param {!String|Number} originalExpenseItemId
 * @param {!Array} splitDetails
 */
export const splitExpenseItem = async ({ originalExpenseItemId, splitDetails }) => {
	const {
		data: { SplitExpenseItem }
	} = await apollo.mutate({
		mutation: gql`
			mutation ($originalExpenseItemId: ID!, $splitDetails: [ExpenseItemInput!]!) {
				SplitExpenseItem(OriginalExpenseItemId: $originalExpenseItemId, SplitDetails: $splitDetails) {
					${columnExpenseItem}
				}
			}
		`,
		variables: {
			originalExpenseItemId,
			splitDetails
		},
		fetchPolicy: 'no-cache'
	});

	return SplitExpenseItem;
};

/**
 * Service to check if expenses image passing in the ocr is already duplicated.
 * @param {Number} entityId
 * @param {Number} entityType
 */
export const checkIfCarbonRowDuplicate = async (entityId, entityType) => {
	if (_.isNil(entityId)) throw new Error('Invalid ID');

	const query = gql`
		query ($entityId: Int!, $entityType: Int!) {
			CheckIfCarbonRowDuplicate(EntityId: $entityId, EntityType: $entityType)
		}
	`;

	const {
		data: { CheckIfCarbonRowDuplicate }
	} = await apollo.query({
		query,
		variables: {
			entityId: +entityId,
			entityType: +entityType
		},
		fetchPolicy: 'no-cache'
	});

	return CheckIfCarbonRowDuplicate;
};

export const expenseDematerializationReport = async (expenseId, sendMail = false) => {
	const query = gql`
		query ($expenseId: ID!, $sendMail: Boolean) {
			ExpenseDematerializationReport(ExpenseId: $expenseId, SendMail: $sendMail)
		}
	`;

	const {
		data: { ExpenseDematerializationReport }
	} = await apollo.query({
		query,
		variables: {
			expenseId: +expenseId,
			sendMail
		},
		fetchPolicy: 'no-cache'
	});

	return ExpenseDematerializationReport;
};

export const cleanWasteExpenseItem = async () => {
	const mutation = gql`
		mutation {
			RemoveUserEmptyExpenseItem
		}
	`;
	const {
		data: { RemoveUserEmptyExpenseItem }
	} = await apollo.mutate({
		mutation,
		fetchPolicy: 'no-cache'
	});

	return RemoveUserEmptyExpenseItem;
};

const columnExpenseItem = `
	expenseId
	createdAt
	updatedAt
	costCenter
	category
	paiementType
	supplierName
	supplierId
	type
	department
	departmentName
	description
	detailedDescription
	subCategory
	date
	id
	images
	amount
	amountTotal
	amountVat
	comment
	km
	kgCoTwo
	fromLocation
	coTwoCategory
	toLocation
	validated
	categoryName
	paiementTypeName
	isInContract
	subType
	additionalSubType
	roundTrip
	kmRate
	user {
		id
		name
		firstName
		departmentName
		functionName
	}
	encoder {
		id
		name
		firstName
		departmentName
		functionName
	}
	projectFlagItems {
		id
		taxeFlags {
			id
			flagId
			value
			name
			short
			factor
			amount
			payCode
			color
			allowanceRate
			bookmark
			category
			digitalSubmission
			disabled
			costCenter
			forScreen
			fieldType
			defaultValue
			jsFormula
		}
		customFlags {
			id
			flagId
			value
			name
			short
			factor
			amount
			payCode
			color
			allowanceRate
			bookmark
			category
			digitalSubmission
			disabled
			costCenter
			forScreen
			fieldType
			defaultValue
			jsFormula
		}
	}
	supplier {
	  name
	}
	carbon {
	  ${PROJECT_CARBON_FIELD}
	}
`;
